import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import AccountInfo from './AccountInfo';
import QuickAction from './QuickAction';
import { forbidden, notFound, resolvePath, useQuery, welcome } from '../../routes';
import {
  fromAccounts,
  fromAccountUsers,
  fromAuth,
  fromOrganizations,
  fromStudioSubscriptions,
  fromSubscriptions,
  fromTestCloudSubscriptions,
  fromTestOpsProject,
  fromTestOpsSubscriptions,
  fromTestResultCount,
  useAppDispatch,
} from '../../store';
import ProductInfo from './ProductInfo';
import LoadingProgress from '../../layout/LoadingProgress';
import { isBillingManagerOrOwner, isManager } from '../../models';
import { fromTestOpsPlatformSubscriptions } from '../../store/rootReducer';
import RunningOutLicenseQuotaDialog from './RunningOutLicenseQuotaDialog';
import { useLaunchDarkly } from '../../launchdarkly';
import { orgCardView } from '../../layout/routes';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    padding: theme.spacing(3, 3),
    width: 'auto',
    height: '100%',
    minHeight: '100vh',
    backgroundColor: '#F0F3FB',
  },
  container: {
    display: 'flex',
    columnGap: theme.spacing(4),
  },
  boxColumn: {
    display: 'column',
  },
}));

const AccountHome = () => {
  const classes = useStyles();
  const { get } = useQuery();
  const [loading, setLoading] = useState(true);
  const orgId = Number(get('orgId'));
  const accountId = Number(get('accountId'));
  const user = useSelector(fromAuth.selectUser);
  const organization = useSelector(fromOrganizations.selectOrganizationById(Number(orgId)));
  const accountUser = useSelector(fromAccountUsers.findAccountUserByAccountId(accountId));
  const testResultCount = useSelector(fromTestResultCount.selectLatestTestResultCount);
  const platformSubscription = useSelector(fromTestOpsPlatformSubscriptions
    .selectTestOpsPlatformSubscriptionByAccountId(
      accountId,
    )).filter(sub => new Date(sub.expiryDate).getTime() > Date.now());
  const dispatch = useAppDispatch();
  const { flags } = useLaunchDarkly();

  useEffect(() => {
    if (!accountId) return;
    const fetchInformation = async () => {
      setLoading(true);
      await Promise.all([
        dispatch(fromAccountUsers.doGetAccountUsersByUserId({ userId: user!!.id })),
        dispatch(fromTestOpsSubscriptions.doGetActiveByAccountId({ accountId })),
        dispatch(fromTestCloudSubscriptions.doGetByAccountId({ accountId })),
        dispatch(fromStudioSubscriptions.doGetActiveByAccountId({ accountId })),
        dispatch(fromSubscriptions
          .doGetActiveSubscriptionsByOrgId({ organizationId: Number(orgId) })),
        dispatch(fromTestOpsPlatformSubscriptions
          .doGetAllTestOpsPlatformSubscriptionsByAccountId({ accountId })),
        dispatch(fromTestResultCount.doGetLatestTestResultCount(orgId)).then(data => {
          let remainUsage = 0;
          if (data.payload) {
            // @ts-ignore
            remainUsage = (data.payload.quota - data.payload.count) / data.payload.quota;
          }
          if (remainUsage > 0.75) {
            localStorage.setItem(`not-show-reach-quota-accId-${accountId}`, 'false');
          }
        }),
      ]).then(() => {
        dispatch(fromTestOpsProject.doCountProjectsByAccountId({ accountId }));
        dispatch(fromAccounts.doCountMembersOfAccount({ id: accountId }));
        dispatch(fromAccounts.doCountOrganizationsOfAccount({ id: accountId }));
      });
      setLoading(false);
    };
    fetchInformation().catch(() => { });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, orgId]);

  if (!orgId && !accountId) {
    return <Navigate to={notFound.path} replace />;
  }
  if (!loading) {
    if (!accountUser) {
      return <Navigate to={welcome.path} replace />;
    }
    if (!isManager(accountUser.role)) {
      return <Navigate to={forbidden.path} replace />;
    }

    let remainUsage = 0;
    if (testResultCount) {
      remainUsage = (testResultCount.quota - testResultCount.count)
        / testResultCount.quota;
    }

    if (remainUsage < 0) {
      remainUsage = 0;
    }

    let skipCheckQuotaAccountAt = JSON.parse(localStorage.getItem(`skip-reach-quota-accId-${accountId}`)!!);
    const dontShowQuotaAccount = JSON.parse(localStorage.getItem(`not-show-reach-quota-accId-${accountId}`) ?? 'false');
    const userLoginTimeStamp = new Date(user?.lastLogin!).valueOf();
    if (skipCheckQuotaAccountAt !== userLoginTimeStamp) {
      localStorage.setItem(`skip-reach-quota-accId-${accountId}`, '0');
      skipCheckQuotaAccountAt = 0;
    }

    const openPopup = remainUsage <= 0.25 && !dontShowQuotaAccount && skipCheckQuotaAccountAt === 0;

    return (
      flags?.newNavigationUIEnabled
        ? (
          <Navigate
            to={
              resolvePath(orgCardView, undefined, { accountId })
            }
            replace
          />
        )
        : (
          <div className={classes.root}>
            <Box className={classes.container}>
              <Box className={classes.boxColumn}>
                <AccountInfo id={accountId} role={accountUser.role} />
                <QuickAction
                  organizationId={orgId}
                  accountId={accountId}
                  role={accountUser.role}
                />
              </Box>
              <ProductInfo
                organizationId={orgId}
                domain={organization?.domain}
                accountId={accountId}
                hasPlatformSubscription={platformSubscription.length > 0}
              />
              {isBillingManagerOrOwner(accountUser.role)
                && openPopup
                && (
                  <RunningOutLicenseQuotaDialog
                    remainUsage={remainUsage}
                    lastLogin={userLoginTimeStamp}
                  />
                )}
            </Box>
          </div>
        )
    );
  }
  return (
    <LoadingProgress />
  );
};

export default AccountHome;
